import { Account } from './account.model';
import { User } from './user.model';

export enum OrderType {
    ENTREGA = 'ENTREGA',
    RECEPCION = 'RECEPCION',
}

export enum OrderStatus {
    CREADA = 'CREADA',
    EN_PROCESO = 'EN_PROCESO',
    COMPLETADA = 'COMPLETADA',
    CANCELADA = 'CANCELADA',
}

export enum OrderLocation {
    DOMICILIO = 'DOMICILIO',
    BODEGA = 'BODEGA',
}

// Fixed catalog (not free-text), Bild listed first — mirrors Catalog's uomOptions pattern.
export const PLATFORM_OPTIONS: string[] = [
    'Bild', 'Amazon', 'Mercado Libre', 'Shopify', 'Walmart', 'Liverpool', 'Coppel', 'Claro Shop', 'Tiktok Shop'
];

// Real brand logos shown in the Orders grid's Plataforma column — SVG where a clean color
// vector was available (Wikimedia Commons' official-logo files, Bild's own app asset);
// cropped/resized PNG for Mercado Libre and Walmart, whose Wikimedia SVGs were an
// incomplete text-only wordmark and an unrecognizable minimalist redesign respectively.
export const PLATFORM_LOGOS: Record<string, string> = {
    'Bild': 'assets/platform-logos/bild.svg',
    'Amazon': 'assets/platform-logos/amazon.svg',
    'Mercado Libre': 'assets/platform-logos/mercado_libre.svg',
    'Shopify': 'assets/platform-logos/shopify.svg',
    'Walmart': 'assets/platform-logos/walmart.svg',
    'Liverpool': 'assets/platform-logos/liverpool.svg',
    'Coppel': 'assets/platform-logos/coppel.svg',
    'Claro Shop': 'assets/platform-logos/claro_shop.svg',
    'Tiktok Shop': 'assets/platform-logos/tiktok_shop.svg'
};

export interface OrderItem {
    orderItemId?: number;
    productId: number;
    sku?: string;
    description?: string;
    variant?: string;
    uom?: string;
    quantity: number;
    comments?: string;
}

export interface Order {
    orderId?: number;
    account?: Account;
    orderType?: OrderType;
    status?: OrderStatus;
    closeReason?: string;
    description?: string;
    assignedUser?: User;
    scheduledDate?: Date;
    titular?: string;
    location?: OrderLocation;
    platform?: string;
    address?: string;
    createdByEmail?: string;
    creationDate?: Date;
    updateDate?: Date;
    completedDate?: Date;
    // True once every item expected by the order has been scanned through the (future)
    // order-driven Agregar Articulos/Salida de Articulos flows — not yet computed
    // automatically, those flows don't exist yet, so this currently just reflects a manual
    // default of false.
    verified?: boolean;
    // Grid summary of the order's lines, batch-aggregated server-side.
    productCount?: number;
    totalArticles?: number;
    items?: OrderItem[];
}

export interface OrderItemInput {
    productId: number;
    quantity: number;
    comments?: string;
}

export interface NewOrder {
    accountId: number;
    orderType: OrderType;
    description?: string;
    scheduledDate: Date;
    titular?: string;
    location: OrderLocation;
    platform: string;
    address?: string;
    items: OrderItemInput[];
}

export interface OrderUpdate {
    description?: string;
    assignedUserId?: number | null;
    scheduledDate?: Date;
    titular?: string;
    location?: OrderLocation;
    address?: string;
    platform?: string;
    // Undefined/omitted leaves the order's items untouched; a list fully replaces them
    // (backend re-validates against stock first).
    items?: OrderItemInput[];
    comment: string;
}

export interface OrderSearchParams {
    accountId?: number;
    orderId?: number;
    orderIdMatchMode?: string;
    description?: string;
    accountName?: string;
    assignedUserId?: number;
    status?: string;
    orderType?: string;
    platform?: string;
    verified?: boolean;
    productCount?: number;
    productCountMatchMode?: string;
    totalArticles?: number;
    totalArticlesMatchMode?: string;
    creationDateFrom?: string;
    creationDateTo?: string;
    updateDateFrom?: string;
    updateDateTo?: string;
    scheduledDateFrom?: string;
    scheduledDateTo?: string;
    completedDateFrom?: string;
    completedDateTo?: string;
    page: number;
    size: number;
    sortBy: string;
    sortDirection: 'ASC' | 'DESC';
}

export interface PageableOrders {
    totalElements: number;
    content: Order[];
}

export interface StockCheckItem {
    productId: number;
    quantity: number;
}

export interface StockCheckRequest {
    accountId: number;
    orderType: OrderType;
    items: StockCheckItem[];
    // When re-validating an existing order's edited items, exclude that order's own current
    // reservation from "reserved" so it isn't double-counted against its own new quantities.
    excludeOrderId?: number;
}

export interface StockViolation {
    productId: number;
    sku?: string;
    requestedQuantity: number;
    availableQuantity: number;
}

export interface OrderMovement {
    orderMovementId?: number;
    movementType?: 'CREATE' | 'UPDATE' | 'COMMENT';
    movementReason?: string;
    user?: string;
    movementTime?: Date;
    changeDetail?: string;
}
